import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RankedListWidget } from '../../dashboard-basic-view/components/RankedListWidget/ranked-list-widget';
import { NoDataWidget } from '../../dashboard-basic-view/components/NoDataWidget/no-data-widget';
import { formatNumber } from '../../../reporting/components/portfolio-report';
import { ReportingPaths } from '../../../reporting/reporting.router';
import { PortfolioWidgetProps } from './portfolio-widget.types';

export function PortfolioTotalOperatingResultWidget({ data, isLoading }: PortfolioWidgetProps) {
  const { t } = useTranslation('widget');
  const currencyCode = data?.currency ?? 'NOK';

  const { total, topHighest, bottomLowest } = useMemo(() => {
    const withEbitda = (data?.buildings ?? []).filter((b) => b.financialEbitda != null);

    // The headline shows the full total amount; the per-building lists are in whole thousands.
    const ranked = withEbitda.map((b) => ({
      id: b.id,
      label: b.name,
      value: Math.round((b.financialEbitda as number) / 1000),
    }));

    return {
      total: withEbitda.reduce((sum, b) => sum + (b.financialEbitda as number), 0),
      topHighest: [...ranked].sort((a, b) => b.value - a.value).slice(0, 3),
      bottomLowest: [...ranked].sort((a, b) => a.value - b.value).slice(0, 3),
    };
  }, [data]);

  if (!isLoading && topHighest.length === 0 && bottomLowest.length === 0)
    return (
      <NoDataWidget
        title={t('portfolioTotalOperatingResult.title')}
        noDataText={t('portfolioTotalOperatingResult.noData')}
      />
    );

  return (
    <RankedListWidget
      to={ReportingPaths.FinancialOverview}
      title={t('portfolioTotalOperatingResult.title')}
      tooltip={t('portfolioTotalOperatingResult.tooltip')}
      value={formatNumber(total / 1000)}
      unit={`k${currencyCode}`}
      rankUnit={` k${currencyCode}`}
      loading={isLoading || !data}
      highListTitle={t('portfolioTotalOperatingResult.topThreeHighest')}
      lowListTitle={t('portfolioTotalOperatingResult.bottomThreeLowest')}
      highListData={topHighest}
      lowListData={bottomLowest}
    />
  );
}
